class FeaturedCards extends HTMLElement {
  constructor() {
    super();
    this.featuredCards = null;
    this.modalOpeners = null;
    this.modalElements = null;
    this.loadingRequests = new Map();
    this.handleOpenModal = this.openModal.bind(this);
    this.selectors = {
      featuredCards: "[data-card]",
      modalElements: "[modal-name]",
      modalOpener: "[data-button]",
      modalContent: "[data-content]",
    };
  }

  connectedCallback() {
    this.cacheHTMLElements();
    this.toggleEventListeners();
  }

  disconnectedCallback() {
    this.toggleEventListeners(true);
  }

  cacheHTMLElements() {
    const featuredCards = [...this.querySelectorAll(this.selectors.featuredCards)];
    if (!featuredCards.length) {
      return;
    }

    this.featuredCards = featuredCards;

    const modalOpeners = [...this.querySelectorAll(this.selectors.modalOpener)];
    if (!modalOpeners.length) {
      return;
    }

    this.modalOpeners = modalOpeners;

    const modalElements = [...this.querySelectorAll(this.selectors.modalElements)];
    if (modalElements.length) {
      this.modalElements = modalElements;
    }
  }

  toggleEventListeners(shouldRemove = false) {
    const method = shouldRemove ? "removeEventListener" : "addEventListener";

    this.modalOpeners?.forEach((opener) => {
      opener[method]("click", this.handleOpenModal);
    });
  }

  findModalElement(modalName) {
    return this.modalElements?.find(
      (modal) => modal.getAttribute("modal-name") === modalName,
    );
  }

  async openModal(event) {
    event.preventDefault();

    const target = event.target;
    const opener = target?.closest(this.selectors.modalOpener);
    const featuredCard = opener?.closest(this.selectors.featuredCards);

    if (!featuredCard) {
      return;
    }

    const modal = this.findModalElement(featuredCard.dataset.modal);
    if (!modal) {
      return;
    }

    modal.showModal();

    try {
      await this.loadModalContent(featuredCard, modal);
    } catch (error) {
      console.error("Featured cards modal failed to load.", error);
    }
  }

  async loadModalContent(featuredCard, modal) {
    const pageUrl = featuredCard.dataset.page;
    const modalContent = modal.querySelector(this.selectors.modalContent);

    if (!pageUrl || !modalContent) {
      return;
    }

    if (modalContent.dataset.loaded === "true") {
      return;
    }

    const existingRequest = this.loadingRequests.get(pageUrl);
    if (existingRequest) {
      try {
        const pageContent = await existingRequest;
        modalContent.innerHTML = pageContent;
        modalContent.dataset.loaded = "true";
      } catch (error) {
        modalContent.innerHTML =
          '<div class="container-small"><p>Sorry, this content could not be loaded right now.</p></div>';
        throw error;
      }
      return;
    }

    const request = this.getPageContent(pageUrl).finally(() => {
      this.loadingRequests.delete(pageUrl);
    });

    this.loadingRequests.set(pageUrl, request);

    try {
      const pageContent = await request;
      modalContent.innerHTML = pageContent;
      modalContent.dataset.loaded = "true";
    } catch (error) {
      modalContent.innerHTML =
        '<div class="container-small"><p>Sorry, this content could not be loaded right now.</p></div>';
      throw error;
    }
  }

  async getPageContent(pageUrl) {
    const response = await fetch(pageUrl, {
      credentials: "same-origin",
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch featured card page: ${response.status}`);
    }

    const responseText = await response.text();
    const parsedDocument = new DOMParser().parseFromString(responseText, "text/html");
    const mainContent = parsedDocument.querySelector("#main-content");

    if (!mainContent) {
      throw new Error("No #main-content node found in featured card page.");
    }

    [...mainContent.querySelectorAll("[data-shopify-editor-section]")].forEach(
      (shopifySection) => {
        shopifySection.removeAttribute("data-shopify-editor-section");
      },
    );

    return mainContent.innerHTML;
  }
}

customElements.define("featured-cards", FeaturedCards);
